import React, { useEffect, useRef, useState } from 'react';
import { CircleCheck, LogIn, LogOut, MapPin, MapPinOff, TriangleAlert, Hand } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFeature } from '../../contexts/FeaturesContext';
import { portalAPI } from '../../services/api';
import { notify, reportLoadError } from '../../utils/notify';
import { toLocalISODate } from '../../utils/dateUtils';
import { addDays } from '../shifts/shiftDates';
import ConsentDialog from './ConsentDialog';
import { readLocation } from './location';

// The guard's shifts: this week and the next two, day by day. With the
// gps_checkin module on, each shift running now also has its buttons
// (check in, I'm here, check out). The phone's location is read only when
// a button is tapped, and only with the consent accepted; without it the
// report still counts, without a location.

const timeOf = (local) => local.slice(11, 16);
const KIND_LABEL = { check_in: 'Checked in', presence: "I'm here", check_out: 'Checked out' };
const REFUSALS = {
  consent_required: 'Accept the location consent first, or report without a location',
  outside_window: 'This shift is not open for this report now',
  already_checked_in: 'You have already checked in',
  already_checked_out: 'You have already checked out',
  not_checked_in: 'Check in first',
  presence_off_or_outside_window: '"I\'m here" is not asked for now',
  shift_not_planned: 'This shift was cancelled',
};

const PortalShifts = () => {
  const { t, locale } = useLanguage();
  const gpsOn = useFeature('gps_checkin');
  const [data, setData] = useState(null);
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(null); // shift id being reported
  const [asking, setAsking] = useState(null); // { shift, kind } waiting for the consent answer
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    let cancelled = false;
    portalAPI.shifts()
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((error) => reportLoadError(error, tRef.current('Error loading your shifts'), { main: true }));
    return () => { cancelled = true; };
  }, [version]);

  const checkin = gpsOn && data && data.checkin ? data.checkin : null;

  const send = async (shift, kind, withLocation) => {
    setBusy(shift.id);
    try {
      const where = withLocation ? await readLocation() : { location_error: 'denied' };
      const res = await portalAPI.report(shift.id, { kind, ...where, device_time: new Date().toISOString() });
      const r = res.data;
      const parts = [t(KIND_LABEL[kind])];
      if (r.location_error) parts.push(t('without a location'));
      else if (r.outside_geofence) parts.push(t('outside the site area'));
      if (r.imprecise) parts.push(t('location not precise'));
      notify(parts.join(' · '), r.outside_geofence || r.location_error ? 'warning' : 'success');
      setVersion((n) => n + 1);
    } catch (error) {
      const code = error.response?.data?.code;
      notify(REFUSALS[code] ? t(REFUSALS[code]) : (error.response?.data?.message || t('Error sending the report')), 'error');
    } finally {
      setBusy(null);
    }
  };

  // the first report asks for the consent; after a "no" for this visit,
  // reports go without a location
  const declined = useRef(false);
  const report = (shift, kind) => {
    if (checkin.consent_accepted) return send(shift, kind, true);
    if (declined.current) return send(shift, kind, false);
    setAsking({ shift, kind });
    return undefined;
  };

  if (!data) return <p className="pt-empty">{t('Loading...')}</p>;

  const today = toLocalISODate();
  const byDay = new Map();
  data.shifts.forEach((s) => {
    const day = s.starts_at.slice(0, 10);
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push(s);
  });
  const dayTitle = (day) => {
    if (day === today) return t('Today');
    if (day === addDays(today, 1)) return t('Tomorrow');
    return new Date(`${day}T12:00:00`).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <div className="pt-page">
      <h1 className="pt-title">{t('My shifts')}</h1>
      {checkin && checkin.presence_interval_minutes > 0 && (
        <p className="pt-hint">{t('During a shift, tap "I\'m here" every {n} minutes, with this page open.', { n: checkin.presence_interval_minutes })}</p>
      )}
      {data.shifts.length === 0 && <p className="pt-empty">{t('No shifts planned for the coming weeks')}</p>}
      {[...byDay.entries()].map(([day, shifts]) => (
        <section key={day} className={`pt-day${day < today ? ' is-past' : ''}`}>
          <h2 className="pt-day__title">{dayTitle(day)}</h2>
          {shifts.map((s) => {
            const state = checkin ? s.state : null;
            const last = checkin && s.checkins.length ? s.checkins[s.checkins.length - 1] : null;
            return (
              <article key={s.id} className="pt-card pt-shift">
                <div className="pt-shift__time">
                  {timeOf(s.starts_at)} – {timeOf(s.ends_at)}{s.ends_at.slice(0, 10) > day ? ' (+1)' : ''}
                  <span className="pt-muted"> · {s.hours} h</span>
                </div>
                <div className="pt-shift__site">{s.site_name}</div>
                <div className="pt-muted">{[s.client_name, s.site_address].filter(Boolean).join(' · ')}</div>
                {s.notes && <p className="pt-shift__notes">{s.notes}</p>}

                {checkin && s.checkins.length > 0 && (
                  <ul className="pt-reports">
                    {s.checkins.map((c) => (
                      <li key={`${c.kind}${c.recorded_at}`} className="pt-report">
                        <CircleCheck aria-hidden="true" /> {t(KIND_LABEL[c.kind])} {timeOf(c.recorded_at)}
                        {c.location_error && <span className="pt-flag"><MapPinOff aria-hidden="true" /> {t('without a location')}</span>}
                        {c.outside_geofence && <span className="pt-flag"><TriangleAlert aria-hidden="true" /> {t('outside the site area')}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {state && state.late && !state.checked_out && <p className="pt-flag pt-flag--block"><TriangleAlert aria-hidden="true" /> {t('Late')}</p>}
                {state && state.can_presence && last && (
                  <p className="pt-muted">{t('Last report at {time}', { time: timeOf(last.recorded_at) })}</p>
                )}

                {state && (state.can_check_in || state.can_presence || state.can_check_out) && (
                  <div className="pt-actions">
                    {state.can_check_in && (
                      <button type="button" className="btn btn--primary pt-big" disabled={busy === s.id} onClick={() => report(s, 'check_in')}>
                        <LogIn aria-hidden="true" /> {t('Check in')}
                      </button>
                    )}
                    {state.can_presence && (
                      <button type="button" className="btn btn--secondary pt-big" disabled={busy === s.id} onClick={() => report(s, 'presence')}>
                        <Hand aria-hidden="true" /> {t("I'm here")}
                      </button>
                    )}
                    {state.can_check_out && (
                      <button type="button" className="btn btn--secondary pt-big" disabled={busy === s.id} onClick={() => report(s, 'check_out')}>
                        <LogOut aria-hidden="true" /> {t('Check out')}
                      </button>
                    )}
                    {busy === s.id && <p className="pt-muted"><MapPin aria-hidden="true" /> {t('Sending...')}</p>}
                  </div>
                )}
              </article>
            );
          })}
        </section>
      ))}

      {asking && (
        <ConsentDialog
          onAccept={async () => {
            const next = asking;
            setAsking(null);
            setData((d) => ({ ...d, checkin: { ...d.checkin, consent_accepted: true } }));
            await send(next.shift, next.kind, true);
          }}
          onDecline={async () => {
            const next = asking;
            setAsking(null);
            declined.current = true;
            await send(next.shift, next.kind, false);
          }}
          onClose={() => setAsking(null)}
        />
      )}
    </div>
  );
};

export default PortalShifts;
